import { compareExpansionWorkOrderDisplay } from "./expansionWorkOrderFormat";
import { readSubsegmentSummaryFromAcf } from "./expansionProjectAcf";
import {
  computeFootageProgressForProject,
  computeInstallationPercentComplete,
  conduitFootageDenominator,
  fiberFootageDenominator,
  readDailyProgressReportsFromAcf,
  resolveInstallationTotals,
} from "./expansionProgressUtils";
import { isCvinExpansion, formatExpansionWorkOrderForDisplay } from "../projectGridColumns";

export function parseFootageNumber(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).replace(/,/g, "").trim();
  const m = s.match(/[\d.]+/);
  if (!m) return null;
  const n = parseFloat(m[0]);
  return Number.isFinite(n) ? n : null;
}

export function workOrderBaseForRow(row) {
  if (row?.work_order_base != null && String(row.work_order_base).trim() !== "") {
    return String(row.work_order_base).trim();
  }
  const wo = String(row?.work_order ?? "").trim();
  const dot = wo.indexOf(".");
  if (dot >= 0) return wo.slice(0, dot).trim();
  return wo || "";
}

export function expansionGroupKeyForRow(row) {
  const base = workOrderBaseForRow(row);
  return base ? base : "(No WO base)";
}

function commonStringPrefix(strings) {
  const list = strings.map((s) => String(s ?? "").trim()).filter(Boolean);
  if (!list.length) return "";
  let prefix = list[0];
  for (let i = 1; i < list.length; i++) {
    while (list[i].indexOf(prefix) !== 0) {
      prefix = prefix.slice(0, -1);
      if (!prefix) return "";
    }
  }
  return prefix;
}

function parseSegmentFromProjectName(name) {
  const n = String(name ?? "").trim();
  if (!n) return "";
  const segMatch =
    n.match(/\bSEG\.?\s*(\d+[a-z]?)\b/i) ||
    n.match(/\bsegment\s*[-#:]?\s*(\d+[a-z]?)\b/i);
  return segMatch ? segMatch[1] : "";
}

function parseSegmentFromLabelPrefix(prefix) {
  return parseSegmentFromProjectName(prefix);
}

function readStoredSegmentFromRow(row) {
  const sub = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
  const seg = sub?.segment ?? sub?.segment_number;
  if (seg == null || String(seg).trim() === "") return "";
  return String(seg).trim();
}

function mostCommonSegmentLabel(values) {
  const counts = new Map();
  for (const raw of values) {
    const seg = String(raw ?? "").trim();
    if (!seg) continue;
    counts.set(seg, (counts.get(seg) || 0) + 1);
  }
  if (!counts.size) return "";
  return [...counts.entries()].sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return String(a[0]).localeCompare(String(b[0]), undefined, { numeric: true, sensitivity: "base" });
  })[0][0];
}

/** MMBI segment number for a parent WO group (not subsegment WO suffixes like .01 / .1). */
export function segmentFromSubsegmentPrefix(children) {
  const rows = Array.isArray(children) ? children : [];
  if (!rows.length) return "";

  const fromSummary = mostCommonSegmentLabel(rows.map(readStoredSegmentFromRow));
  if (fromSummary) return fromSummary;

  const fromNames = mostCommonSegmentLabel(rows.map((r) => parseSegmentFromProjectName(r?.name)));
  if (fromNames) return fromNames;

  const names = rows.map((r) => String(r?.name ?? "").trim()).filter(Boolean);
  if (names.length) {
    const fromNamePrefix = parseSegmentFromLabelPrefix(commonStringPrefix(names).trim());
    if (fromNamePrefix) return fromNamePrefix;
  }

  const unique = new Set(
    rows
      .map((r) => readStoredSegmentFromRow(r) || parseSegmentFromProjectName(r?.name))
      .filter(Boolean)
  );
  if (unique.size === 1) return [...unique][0];
  return "";
}

export function countExpansionSubsegments(children) {
  return (Array.isArray(children) ? children : []).filter((row) => !isExpansionParentShellRow(row)).length;
}

export function expansionStatsGroup(row) {
  const subSummary = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
  const reports = readDailyProgressReportsFromAcf(row?.acf_groups_json);
  const normalized = resolveInstallationTotals(subSummary, reports);
  if (normalized && typeof normalized === "object") {
    return {
      construction_footage: normalized.construction_footage,
      override_footage: normalized.override_footage,
      completed_footage: normalized.completed_footage,
      completed_conduit_footage: normalized.completed_conduit_footage,
      completed_fiber_footage: normalized.completed_fiber_footage,
      completed_proofing_footage: normalized.completed_proofing_footage,
      caltrans_footage: normalized.caltrans_footage,
      handholes: normalized.handholes,
    };
  }
  const j = row?.acf_groups_json;
  if (!j || typeof j !== "object") return null;
  return j.expansion_segment_statistics ?? j["Expansion Segment Statistics"] ?? null;
}

export function computeExpansionGroupStats(children) {
  let overrideTotal = 0;
  let constructionTotal = 0;
  let completedTotal = 0;
  let completedConduitTotal = 0;
  let completedFiberTotal = 0;
  let completedProofingTotal = 0;
  let handholesTotal = 0;
  let hasOverride = false;
  let hasConstruction = false;
  let hasCompleted = false;
  let hasCompletedConduit = false;
  let hasCompletedFiber = false;
  let hasCompletedProofing = false;
  let hasHandholes = false;
  const phases = {};
  const statuses = {};

  for (const r of children) {
    const sub = readSubsegmentSummaryFromAcf(r?.acf_groups_json);
    const progress = computeFootageProgressForProject(r);
    const ov = parseFootageNumber(sub?.override_footage);
    const cf = parseFootageNumber(sub?.construction_footage);
    const done = progress.completedTotal ?? 0;
    if (ov != null) {
      overrideTotal += ov;
      hasOverride = true;
    }
    if (cf != null) {
      constructionTotal += cf;
      hasConstruction = true;
    }
    if (done > 0 || progress.inAccounting) {
      completedTotal += done;
      hasCompleted = true;
    }
    completedConduitTotal += progress.conduitCompleted ?? 0;
    completedFiberTotal += progress.fiberCompleted ?? 0;
    hasCompletedConduit = true;
    hasCompletedFiber = true;
    if (progress.proofingCompleted > 0) {
      completedProofingTotal += progress.proofingCompleted;
      hasCompletedProofing = true;
    }
    if (progress.vaults > 0) {
      handholesTotal += progress.vaults;
      hasHandholes = true;
    }
    const phase = sub?.phase ?? r?.phase_label ?? "Unknown";
    phases[String(phase)] = (phases[String(phase)] || 0) + 1;
    const st = sub?.status ?? r?.status ?? "Unknown";
    statuses[String(st)] = (statuses[String(st)] || 0) + 1;
  }

  const installationProgress = computeExpansionGroupFootageProgress(children);

  return {
    projectCount: children.length,
    overrideFootageTotal: hasOverride ? overrideTotal : null,
    constructionFootageTotal: hasConstruction ? constructionTotal : null,
    completedFootageTotal: hasCompleted ? completedTotal : null,
    completedConduitFootageTotal: hasCompletedConduit ? completedConduitTotal : null,
    completedFiberFootageTotal: hasCompletedFiber ? completedFiberTotal : null,
    completedProofingFootageTotal: hasCompletedProofing ? completedProofingTotal : null,
    handholesTotal: hasHandholes ? handholesTotal : null,
    percentComplete: installationProgress.percentComplete,
    phases,
    statuses,
  };
}

/** True when a project title includes CDT (excluded from segment overall %). */
export function expansionRowTitleHasCdt(row) {
  const title = row?.name || row?.project_name || "";
  return /CDT/i.test(String(title || ""));
}

/** Aggregated conduit/fiber installation progress for a segment group. */
export function computeExpansionGroupFootageProgress(children) {
  let fiberDenominatorTotal = 0;
  let conduitDenominatorTotal = 0;
  let conduitCompleted = 0;
  let fiberCompleted = 0;
  let proofingCompleted = 0;
  let vaults = 0;
  let hasFiberDenominator = false;
  let hasConduitDenominator = false;

  for (const row of children) {
    // CDT interconnect subsegments are omitted from segment-level conduit/fiber %.
    if (expansionRowTitleHasCdt(row)) continue;

    const sub = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
    const fiberDenom = fiberFootageDenominator(sub);
    const conduitDenom = conduitFootageDenominator(sub);
    if (fiberDenom != null) {
      fiberDenominatorTotal += fiberDenom;
      hasFiberDenominator = true;
    }
    if (conduitDenom != null) {
      conduitDenominatorTotal += conduitDenom;
      hasConduitDenominator = true;
    }
    const progress = computeFootageProgressForProject(row);
    conduitCompleted += progress.conduitCompleted ?? 0;
    fiberCompleted += progress.fiberCompleted ?? 0;
    proofingCompleted += progress.proofingCompleted ?? 0;
    vaults += progress.vaults ?? 0;
  }

  const fiberDenominator = hasFiberDenominator ? fiberDenominatorTotal : null;
  const conduitDenominator = hasConduitDenominator ? conduitDenominatorTotal : null;
  const completedTotal = conduitCompleted + fiberCompleted;
  const conduitPct =
    conduitDenominator != null && conduitDenominator > 0
      ? Math.min(100, (conduitCompleted / conduitDenominator) * 100)
      : 0;
  const fiberPct =
    fiberDenominator != null && fiberDenominator > 0
      ? Math.min(100, (fiberCompleted / fiberDenominator) * 100)
      : 0;
  const percentComplete = computeInstallationPercentComplete({
    conduitPct,
    fiberPct,
    conduitDenominator,
    fiberDenominator,
  });

  return {
    denominator: fiberDenominator,
    fiberDenominator,
    conduitDenominator,
    conduitCompleted,
    fiberCompleted,
    proofingCompleted,
    completedTotal,
    vaults,
    percentComplete,
    conduitPct,
    fiberPct,
  };
}

export function formatExpansionGroupTitle(woBase, segment) {
  const wo = woBase && String(woBase).trim();
  if (!wo || wo.startsWith("(")) return woBase || "Segment";
  const seg = segment && String(segment).trim();
  if (!seg) return wo;
  return `${wo} - Segment ${seg}`;
}

export function segmentSortValue(label) {
  const s = String(label || "").trim();
  const m1 = s.match(/\bsegment\s*[-#:]?\s*(\d+)/i);
  if (m1) return Number(m1[1]);
  const m2 = s.match(/\b(\d+)\b/);
  if (m2) return Number(m2[1]);
  return Number.POSITIVE_INFINITY;
}

export function expansionParentWoSortCompare(keyA, keyB) {
  const na = parseFloat(String(keyA));
  const nb = parseFloat(String(keyB));
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
  const sa = segmentSortValue(keyA);
  const sb = segmentSortValue(keyB);
  if (Number.isFinite(sa) && Number.isFinite(sb) && sa !== sb) return sa - sb;
  return String(keyA).localeCompare(String(keyB), undefined, { numeric: true, sensitivity: "base" });
}

export function formatExpansionFootage(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${n.toLocaleString()} ft`;
}

export function isExpansionParentShellRow(row) {
  const wo = String(row?.work_order ?? "").trim();
  return Boolean(wo) && !wo.includes(".");
}

export function filterExpansionRowsForBase(rows, base) {
  const woBase = String(base || "").trim();
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (!isCvinExpansion(row)) return false;
    return workOrderBaseForRow(row) === woBase;
  });
}

export function buildExpansionSegmentGroups(rows) {
  const buckets = new Map();
  for (const row of rows) {
    const key = expansionGroupKeyForRow(row);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(row);
  }
  return [...buckets.entries()]
    .map(([key, children]) => {
      const segment = segmentFromSubsegmentPrefix(children);
      const stats = computeExpansionGroupStats(children);
      return {
        key,
        woBase: key.startsWith("(") ? "" : key,
        segment,
        title: formatExpansionGroupTitle(key, segment),
        children: [...children].sort((a, b) =>
          compareExpansionWorkOrderDisplay(
            formatExpansionWorkOrderForDisplay(a),
            formatExpansionWorkOrderForDisplay(b)
          )
        ),
        stats,
      };
    })
    .sort((a, b) => expansionParentWoSortCompare(a.key, b.key));
}
